import React, { useState, useEffect } from 'react';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, UserPlus, Shield, Users, Coins, Receipt, Eye, EyeOff, Pencil, Trash2, Check, X, Power } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../lib/axios';

export default function AdminManagementPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [form, setForm] = useState({ email: '', username: '', password: '' });
  const [editForm, setEditForm] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchAdmins = async () => {
    try {
      const res = await axios.get('/admin/admins');
      setAdmins(res.data.admins || []);
    } catch (e) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreate = async () => {
    if (!form.email || !form.username || !form.password) {
      toast.error('All fields are required');
      return;
    }
    try {
      await axios.post('/admin/admins', form);
      toast.success(`Admin "${form.username}" created`);
      setForm({ email: '', username: '', password: '' });
      setShowCreate(false);
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await axios.put(`/admin/admins/${admin.id}`, { is_active: !admin.is_active });
      toast.success(`Admin ${admin.is_active ? 'disabled' : 'enabled'}`);
      fetchAdmins();
    } catch (e) {
      toast.error('Failed to update admin');
    }
  };

  const handleEdit = async () => {
    if (!editAdmin) return;
    try {
      await axios.put(`/admin/admins/${editAdmin.id}`, editForm);
      toast.success('Admin updated');
      setEditAdmin(null);
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const openEdit = (admin) => {
    setEditAdmin(admin);
    setEditForm({
      username: admin.username,
      allow_sub_admin: admin.allow_sub_admin || false,
      password: '',
    });
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin "${admin.username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/admin/admins/${admin.id}`);
      toast.success('Admin deleted');
      setAdmins(prev => prev.filter(a => a.id !== admin.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  return (
    <div className="min-h-screen bg-[#07070f] text-white">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="lg:ml-72 flex flex-col min-h-screen">
        <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
        <ScrollArea className="flex-1 p-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-[#FFD700]/10">
                  <Crown className="w-7 h-7 text-[#FFD700]" />
                </div>
                <div>
                  <h1 className="text-2xl font-black text-white">ADMIN CONTROL</h1>
                  <p className="text-sm text-gray-400">Manage admin accounts and permissions</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreate(!showCreate)}
                className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm text-black"
                style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B00)' }}
                data-testid="create-admin-btn"
              >
                <UserPlus className="w-4 h-4" /> CREATE ADMIN
              </motion.button>
            </div>

            {/* Create Admin Form */}
            <AnimatePresence>
              {showCreate && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 overflow-hidden">
                  <Card className="bg-[#0d0d15] border-0 p-5 relative" data-testid="create-admin-form">
                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">New Admin Account</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input type="text" placeholder="Username" value={form.username} onChange={e => setForm({...form, username: e.target.value})} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm" data-testid="admin-username-input" />
                      <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm" data-testid="admin-email-input" />
                      <input type="password" placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm" data-testid="admin-password-input" />
                    </div>
                    <div className="flex gap-2 mt-4">
                      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleCreate} className="px-5 py-2.5 rounded-xl font-bold text-sm text-black" style={{ background: 'linear-gradient(135deg, #00FF88, #00F5FF)' }} data-testid="confirm-create-admin">
                        <Check className="w-4 h-4 inline mr-1" /> Create
                      </motion.button>
                      <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10">Cancel</Button>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Total Admins', count: admins.length, color: '#FFD700', icon: Crown },
                { label: 'Active', count: admins.filter(a => a.is_active).length, color: '#00FF88', icon: Shield },
                { label: 'Disabled', count: admins.filter(a => !a.is_active).length, color: '#FF3366', icon: EyeOff },
                { label: 'Total Employees', count: admins.reduce((s, a) => s + (a.employee_count || 0), 0), color: '#9945FF', icon: Users },
              ].map(({ label, count, color, icon: Icon }) => (
                <div key={label} className="p-3 rounded-xl bg-[#0d0d15]" style={{ border: `1px solid ${color}20` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color }} />
                    <span className="text-[11px] font-semibold text-gray-400 uppercase">{label}</span>
                  </div>
                  <p className="text-2xl font-black" style={{ color }}>{count}</p>
                </div>
              ))}
            </div>

            {/* Admin List */}
            <Card className="bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Admin</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Employees</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Players</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Transactions</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Status</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin) => {
                      const isSuperAdmin = admin.role === 'super_admin';
                      return (
                        <tr key={admin.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`admin-row-${admin.id}`}>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              {isSuperAdmin && <Crown className="w-4 h-4 text-[#FFD700] shrink-0" />}
                              <span className="text-sm font-bold text-white">{admin.username}</span>
                              {isSuperAdmin && <Badge className="bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30 text-[9px]">OWNER</Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-400">{admin.email}</td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-sm font-mono text-[#9945FF]">{admin.employee_count || 0}</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-sm font-mono text-[#00F5FF]">{admin.player_count || 0}</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-sm font-mono text-[#FF00E5]">{admin.transaction_count || 0}</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Badge className={`text-[10px] ${admin.is_active ? 'bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/30' : 'bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/30'}`}>
                              {admin.is_active ? 'Active' : 'Disabled'}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 text-right">
                            {!isSuperAdmin && (
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => openEdit(admin)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid={`edit-admin-${admin.id}`}>
                                  <Pencil className="w-3.5 h-3.5 text-[#9945FF]" />
                                </button>
                                <button onClick={() => handleToggleActive(admin)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid={`toggle-admin-${admin.id}`}>
                                  <Power className={`w-3.5 h-3.5 ${admin.is_active ? 'text-[#FF3366]' : 'text-[#00FF88]'}`} />
                                </button>
                                <button onClick={() => handleDelete(admin)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid={`delete-admin-${admin.id}`}>
                                  <Trash2 className="w-3.5 h-3.5 text-[#FF3366]" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </ScrollArea>
      </div>

      {/* Edit Admin Modal */}
      <AnimatePresence>
        {editAdmin && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setEditAdmin(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={e => e.stopPropagation()} className="w-full max-w-md">
              <Card className="bg-[#0d0d15] border border-[#FFD700]/30 overflow-hidden" data-testid="edit-admin-modal">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
                <div className="p-5">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Pencil className="w-5 h-5 text-[#FFD700]" /> Edit: {editAdmin.username}
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Username</label>
                      <input type="text" value={editForm.username || ''} onChange={e => setEditForm({...editForm, username: e.target.value})} className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">New Password (leave blank to keep)</label>
                      <input type="password" value={editForm.password || ''} onChange={e => setEditForm({...editForm, password: e.target.value})} className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm" placeholder="Enter new password" />
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-white/5">
                      <input type="checkbox" checked={editForm.allow_sub_admin || false} onChange={e => setEditForm({...editForm, allow_sub_admin: e.target.checked})} className="rounded" />
                      <label className="text-sm text-gray-300">Allow sub-admin creation</label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleEdit} className="flex-1 py-3 rounded-xl font-bold text-black text-sm" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B00)' }} data-testid="save-admin-edit">
                      <Check className="w-4 h-4 inline mr-1" /> SAVE
                    </motion.button>
                    <Button variant="outline" onClick={() => setEditAdmin(null)} className="flex-1 border-white/10">Cancel</Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
